import { toast } from '@/components/toast';
import { audiosDns, maxAudioSizeInBytes, projectDns, projectsBucket } from '@/environment';
import { AudioData, useUploadAudio } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { formatS3Url } from '@/utils/s3';
import { annoynmousUserToEditorPagePostSsoReturnTo, renameFile } from '@/utils/utils';
import { nanoid } from 'nanoid';

const MAX_DURATION = 5 * 60;

export default function ({ resultUrl, onUploadComplete }: { resultUrl?: string; onUploadComplete?: (audioData: AudioData) => void }) {
    const mixpanel = useAnalytics();
    const { isAnonymous, loginWithRedirect } = useAuth();
    const { mutateAsync: uploadAudio } = useUploadAudio(onUploadComplete);

    return {
        upload: async (file?: File, blob?: Blob): Promise<{ sourceUrl: string; listenUrl: string; duration: number }> => {
            if (isAnonymous) {
                mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-audio', isAnonymous });
                loginWithRedirect({
                    screen_hint: 'signup',
                    appState: { returnTo: annoynmousUserToEditorPagePostSsoReturnTo },
                });
            } else if (file || blob) {
                if (file && !file.type.match(/^audio\/.*$|^video\/.*$/)) {
                    toast.error({ title: `Invalid audio type: ${file.type}` });
                } else if (file && file.size > maxAudioSizeInBytes) {
                    toast.error({ title: `Audio file must be less than ${Math.floor(maxAudioSizeInBytes * 0.00001) / 10}MB` });
                } else {
                    if (file) {
                        const audio = new Audio();
                        audio.src = URL.createObjectURL(file);

                        await new Promise<void>(resolve => {
                            audio.addEventListener('loadedmetadata', () => {
                                resolve();
                            });
                        });

                        if (audio.duration > MAX_DURATION) {
                            toast.info({ title: 'Audio file should be up to 5 minutes', toastId: `upload-audio-error` });
                            return { listenUrl: '', sourceUrl: '', duration: -1 };
                        }
                    }
                    if (!file) {
                        file = new File([blob!], 'voice.webm', {
                            type: 'audio/webm;codecs=opus',
                        });
                    }
                    const { url, duration } = await uploadAudio({ audio: renameFile(file, `audio_${nanoid()}`), resultUrl });
                    mixpanel?.track('audio-upload', { event: 'success' });

                    const isProjectBucket = resultUrl?.includes(projectsBucket);
                    const listenUrl = formatS3Url(isProjectBucket ? projectDns : audiosDns, url);

                    return { listenUrl, sourceUrl: url, duration };
                }
            }

            return { listenUrl: '', sourceUrl: '', duration: -1 };
        },
    };
}
